import React from 'react';
import { CloseOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { BaseAlertBanner, BaseButton } from '@components';
import { AbsoluteWrapper as Wrapper } from '@components/alert-banner/styled.tsx';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useQuestionsCsv } from '@hooks/useQuestionsCsv.ts';
import { useLingui } from '@lingui/react/macro';
import {
    clearQuestionsCsvImportState,
    setIsUploadQuestionsCsvModalOpened,
    setUploadedQuestionsCsvFile,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/model/creatorElementsSlice.ts';

import { Actions, Description, DescriptionWrapper } from './styled';

interface Props {
    filename: string;
}

export const QuestionsCsvImportFailedBanner: React.FC<Props> = ({ filename }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();
    const { exportQuestionsCsvTemplate, downloadQuestionsCsvIssues } = useQuestionsCsv();
    const questionsCsvIssuesFileContent = useAppSelector(
        (state) => state.adminCenterCreatorElements.questionsCsvIssuesFileContent,
    );

    const handleTryDifferentFile = () => {
        dispatch(clearQuestionsCsvImportState());
        dispatch(setUploadedQuestionsCsvFile(null));
        dispatch(setIsUploadQuestionsCsvModalOpened(true));
    };

    const handleClose = () => {
        dispatch(clearQuestionsCsvImportState());
    };

    return (
        <Wrapper $width={'50%'}>
            <BaseAlertBanner
                closable={{
                    closeIcon: <CloseOutlined />,
                    onClose: handleClose,
                }}
                type="error"
                title={`${filename} - import failed`}
                description={
                    <DescriptionWrapper>
                        <Description>
                            <div>{t`The CSV file could not be imported. Please check the following:`}</div>
                            <ul>
                                <li>{t`File format is valid CSV`}</li>
                                <li>{t`Column header matches the template`}</li>
                                <li>{t`QuizType is 'Final Quiz' or 'Practice quiz'`}</li>
                                <li>{t`Each row has at least one variation and one answer`}</li>
                            </ul>
                        </Description>
                        <Actions>
                            <BaseButton onClick={handleTryDifferentFile} $customType={'outline'}>
                                {t`Try different file`}
                            </BaseButton>
                            <BaseButton onClick={exportQuestionsCsvTemplate} $customType={'link'}>
                                {t`Download CSV template`}
                            </BaseButton>
                            {questionsCsvIssuesFileContent !== null && (
                                <BaseButton onClick={downloadQuestionsCsvIssues} $customType={'link'}>
                                    {t`Download file with errors`}
                                </BaseButton>
                            )}
                        </Actions>
                    </DescriptionWrapper>
                }
                icon={<ExclamationCircleFilled />}
            />
        </Wrapper>
    );
};
